import type { TankParameterKeys, DepartmentParameterKeys, AnnualParameterKey, PeriodParameterKey, MinExchangeTimeKey } from '@/modules/axios/types/types'
import { HOG_FACTOR } from '@/modules/axios/utils/event-processing/steps/computeAnnualValues'

type AllKeys = TankParameterKeys | DepartmentParameterKeys | AnnualParameterKey | PeriodParameterKey | MinExchangeTimeKey

export type DashboardParameterConfig = {
  label: string
  description?: string
  fromUnit?: string
  toUnit?: string
  // EUR amount, shown converted to the user's currency instead of with a unit
  money?: boolean
  // Compared the other way round, like a cost
  lowerIsBetter?: boolean
}

export const dashboardConfig: Record<AllKeys, DashboardParameterConfig> = {
  // Fish (selected time window)
  biomass: { label: 'Maximum biomass', description: 'Maximum total fish biomass within the selected time window', fromUnit: 'g', toUnit: 'kg' },
  fishNumber: { label: 'Peak fish number', description: 'Maximum number of fish within the selected time window', fromUnit: 'pcs' },
  fishAvgWeight: { label: 'Peak fish weight', description: 'Maximum average fish weight within the selected time window', fromUnit: 'g' },
  growth: { label: 'Peak growth', description: 'Maximum daily growth within the selected time window', fromUnit: 'g' },
  growthRate: { label: 'Peak growth rate', description: 'Maximum daily specific growth rate within the selected time window' },
  grossBiomassProduction: { label: 'Peak gross biomass production', description: 'Maximum daily gross biomass added by growth within the selected time window', fromUnit: 'g', toUnit: 'kg' },
  netBiomassProduction: { label: 'Peak net biomass production', description: 'Maximum daily net biomass production (growth minus mortality) within the selected time window', fromUnit: 'g', toUnit: 'kg' },
  grossBiomassProductionAccumulated: { label: 'Gross biomass production', description: 'Cumulative gross growth within the selected time window', fromUnit: 'g', toUnit: 'kg' },
  netBiomassProductionAccumulated: { label: 'Net biomass production', description: 'Cumulative growth minus mortality losses within the selected time window', fromUnit: 'g', toUnit: 'kg' },
  fishLoss: { label: 'Peak fish loss', description: 'Maximum daily fish mortality count within the selected time window', fromUnit: 'pcs' },
  fishBiomassLoss: { label: 'Peak biomass loss', description: 'Maximum daily biomass lost to mortality within the selected time window', fromUnit: 'g', toUnit: 'kg' },
  destroyedBiomass: { label: 'Peak destroyed biomass', description: 'Maximum daily biomass destroyed within the selected time window', fromUnit: 'g', toUnit: 'kg' },
  deliveredBiomass: { label: 'Peak delivered biomass', description: 'Maximum daily biomass delivered out of the system within the selected time window', fromUnit: 'g', toUnit: 'kg' },
  deliveredFishNumber: { label: 'Peak delivered fish number', description: 'Maximum daily fish count delivered out of the system within the selected time window', fromUnit: 'pcs' },
  fishDensity: { label: 'Peak fish density', description: 'Maximum fish density in the tank within the selected time window', fromUnit: 'g/m3', toUnit: 'kg/m3' },
  fishDensityAverage: { label: 'Peak average density', description: 'Maximum of the average fish density across all tanks within the selected time window', fromUnit: 'g/m3', toUnit: 'kg/m3' },
  fishDensityPeak: { label: 'Peak fish density', description: 'Highest fish density found in any single tank within the selected time window', fromUnit: 'g/m3', toUnit: 'kg/m3' },

  // Water Flow (selected time window)
  totalWaterFlow: { label: 'Peak total water flow', description: 'Maximum combined water flow within the selected time window', fromUnit: 'l/min' },
  makeUpWaterFlow: { label: 'Peak makeupwater flow', description: 'Maximum fresh makeup water flow within the selected time window', fromUnit: 'l/min' },
  recirculationFlow: { label: 'Peak recirculation flow', description: 'Maximum recirculated water flow within the selected time window', fromUnit: 'l/min' },
  makeUpWaterFreshConsumption: { label: 'Peak freshwater consumption', description: 'Maximum freshwater consumption within the selected time window', fromUnit: 'l/min' },
  makeUpWaterSeaConsumption: { label: 'Peak seawater consumption', description: 'Maximum seawater consumption within the selected time window', fromUnit: 'l/min' },
  reuseDegree: { label: 'Peak reuse degree', description: 'Maximum water reuse percentage within the selected time window', fromUnit: '%' },
  waterExchangeTime: { label: 'Peak water exchange time', description: 'Maximum water exchange time within the selected time window', fromUnit: 'min' },
  waterExchangeTimeMakeupwater: { label: 'Peak water exchange time (makeupwater)', description: 'Maximum water exchange time using makeupwater flow only within the selected time window', fromUnit: 'min' },
  waterExchangeTimeMin: { label: 'Min water exchange time', description: 'Minimum water exchange time within the selected time window', fromUnit: 'min' },
  waterExchangeTimeMakeupwaterMin: { label: 'Min water exchange time (makeupwater)', description: 'Minimum water exchange time using makeupwater flow only within the selected time window', fromUnit: 'min' },

  // Consumables and water parameters (selected time window)
  feedConsumption: { label: 'Peak feed consumption', description: 'Maximum daily feed consumption within the selected time window', fromUnit: 'g/day', toUnit: 'kg/day' },
  o2Consumption: { label: 'Peak O2 consumption', description: 'Maximum oxygen consumption rate within the selected time window', fromUnit: 'mg/min', toUnit: 'kg/h' },
  co2Concentration: { label: 'Peak CO2 concentration', description: 'Maximum CO2 concentration within the selected time window', fromUnit: 'mg/l' },
  tanConcentration: { label: 'Peak TAN concentration', description: 'Maximum total ammonia nitrogen concentration within the selected time window', fromUnit: 'mg/l' },
  nitrateConcentration: { label: 'Peak nitrate concentration', description: 'Maximum nitrate concentration within the selected time window', fromUnit: 'mg/l' },
  suspendedSolidsConcentration: { label: 'Peak suspended solids concentration', description: 'Maximum suspended solids concentration within the selected time window', fromUnit: 'mg/l' },
  oxygenPerFeed: { label: 'Peak O2 per kg feed', description: 'Maximum oxygen consumption per kg of feed within the selected time window', fromUnit: 'g/kg' },
  co2ConcentrationPeak: { label: 'Peak CO2 concentration', description: 'Highest CO2 concentration found in any single tank within the selected time window', fromUnit: 'mg/l' },
  tanConcentrationPeak: { label: 'Peak TAN concentration', description: 'Highest TAN concentration found in any single tank within the selected time window', fromUnit: 'mg/l' },
  nitrateConcentrationPeak: { label: 'Peak nitrate concentration', description: 'Highest nitrate concentration found in any single tank within the selected time window', fromUnit: 'mg/l' },
  suspendedSolidsConcentrationPeak: { label: 'Peak suspended solids concentration', description: 'Highest suspended solids concentration found in any single tank within the selected time window', fromUnit: 'mg/l' },

  // Waste Production (selected time window)
  co2Production: { label: 'Peak CO2 production', description: 'Maximum CO2 production rate within the selected time window', fromUnit: 'mg/min', toUnit: 'kg/h' },
  tanProduction: { label: 'Peak TAN production', description: 'Maximum total ammonia nitrogen production rate within the selected time window', fromUnit: 'mg/min', toUnit: 'kg/h' },
  nitrateProduction: { label: 'Peak nitrate production', description: 'Maximum nitrate production rate within the selected time window', fromUnit: 'mg/min', toUnit: 'kg/h' },
  suspendedSolidsProduction: { label: 'Peak suspended solids production', description: 'Maximum suspended solids production rate within the selected time window', fromUnit: 'mg/min', toUnit: 'kg/h' },

  // Period delivery (selected time window)
  lfeDelivered: { label: 'LFE delivered', description: 'Total delivered biomass of live fish equivalent within the selected time window', fromUnit: 'g', toUnit: 'kg' },
  fishNumberDelivered: { label: 'Fish number delivered', description: 'Total number of fish delivered within the selected time window', fromUnit: 'pcs' },

  // Fish value (selected time window, priced by the concept's fish price table)
  fishPurchaseCost: { label: 'Fish purchase cost', description: 'Value of the fish inserted within the selected time window, per fish from the smolt price table', money: true, lowerIsBetter: true },
  fishSalesRevenue: { label: 'Fish sales revenue', description: 'Value of the fish delivered within the selected time window, per kg from the harvest price table', money: true },
  fishNetValue: { label: 'Net fish value', description: 'Fish sales revenue minus fish purchase cost within the selected time window', money: true },
  fishStandingValue: { label: 'Peak standing fish value', description: 'Highest value of the fish in the tanks on any day within the selected time window, per kg from the harvest price table at their average weight', money: true },

  // Annual delivery (full production cycle)
  lfePerYear: { label: 'LFE delivered per year', description: 'Annualized deliveries of live fish equivalent over the lifetime of the facility', fromUnit: 'g', toUnit: 'kg' },
  hogPerYear: { label: 'HOG delivered per year', description: `Annualized deliveries of head-on gutted over the lifetime of the facility (LFE × ${HOG_FACTOR})`, fromUnit: 'g', toUnit: 'kg' },
  lfePerVolume: { label: 'LFE per volume', description: 'Annualized deliveries of live fish equivalent per cubic meter over the lifetime of the facility', fromUnit: 'g/m3', toUnit: 'kg/m3' },
  hogPerVolume: { label: 'HOG per volume', description: `Annualized deliveries of head-on gutted per cubic meter over the lifetime of the facility (LFE × ${HOG_FACTOR})`, fromUnit: 'g/m3', toUnit: 'kg/m3' },
  averageDensity: { label: 'Average density', description: 'Average fish density within the selected time window (total biomass / volume / days)', fromUnit: 'g/m3', toUnit: 'kg/m3' },

  // Unused in dashboard (required by AllKeys union)
  waterTemperature: { label: 'Water temperature', fromUnit: '°C' },
  accumulatedDegreeDays: { label: 'Degree days', fromUnit: '°D' },
  accumulatedDegreeDaysPeak: { label: 'Peak degree days', fromUnit: '°D' },
  o2MakeUpWater: { label: 'Makeupwater from O2', fromUnit: 'l/min' },
  co2MakeUpWater: { label: 'Makeupwater from CO2', fromUnit: 'l/min' },
  tanMakeUpWater: { label: 'Makeupwater from TAN', fromUnit: 'l/min' },
  nitrateMakeUpWater: { label: 'Makeupwater from nitrate', fromUnit: 'l/min' },
  suspendedSolidsMakeUpWater: { label: 'Makeupwater from suspended solids', fromUnit: 'l/min' },
  co2Recirculation: { label: 'Recirculation from CO2', fromUnit: 'l/min' },
  tanRecirculation: { label: 'Recirculation from TAN', fromUnit: 'l/min' },
  nitrateRecirculation: { label: 'Recirculation from nitrate', fromUnit: 'l/min' },
  suspendedSolidsRecirculation: { label: 'Recirculation from suspended solids', fromUnit: 'l/min' }
}

// The default facility-level key numbers shown on the Production Schedule header (EventRoadmapHeader.vue,
// user-selectable via the roadmap "Filter header" menu) and pre-selected as a starting point for comparing
// Axios concepts against each other (issue #2405).
export const facilityHeaderCardKeys = [
  'grossBiomassProductionAccumulated',
  'netBiomassProductionAccumulated',
  'biomass',
  'averageDensity',
  'lfeDelivered',
  'fishNumberDelivered'
] as const

// ─── Facility-level parameter groupings ─────────────────────────────────────
// Shared by AxiosDashboard.vue (Design Parameters > Facility) and the concept
// compare page (issue #2405), so both surfaces offer the exact same parameters.

export const periodFishKeys: PeriodParameterKey[] = ['lfeDelivered', 'fishNumberDelivered']
export const fishValueKeys: (PeriodParameterKey | DepartmentParameterKeys)[] = [
  'fishPurchaseCost',
  'fishSalesRevenue',
  'fishNetValue',
  'fishStandingValue'
]
export const annualFishKeys: AnnualParameterKey[] = ['lfePerYear', 'hogPerYear', 'lfePerVolume', 'hogPerVolume']
export const minExchangeTimeKeys: MinExchangeTimeKey[] = ['waterExchangeTimeMin', 'waterExchangeTimeMakeupwaterMin']
export const extraFishKeys: MinExchangeTimeKey[] = ['averageDensity']

export const deptFishKeys: DepartmentParameterKeys[] = [
  'biomass',
  'fishNumber',
  'fishAvgWeight',
  'growth',
  'grossBiomassProductionAccumulated',
  'netBiomassProductionAccumulated',
  'fishLoss',
  'fishBiomassLoss',
  'deliveredBiomass',
  'fishDensityAverage',
  'fishDensityPeak'
]

export const tankFishKeys: TankParameterKeys[] = [
  'biomass',
  'fishNumber',
  'fishAvgWeight',
  'growth',
  'grossBiomassProductionAccumulated',
  'netBiomassProductionAccumulated',
  'fishLoss',
  'fishBiomassLoss',
  'deliveredBiomass',
  'fishDensity'
]

export const deptWaterKeys: DepartmentParameterKeys[] = [
  'totalWaterFlow',
  'makeUpWaterFlow',
  'recirculationFlow',
  'makeUpWaterFreshConsumption',
  'makeUpWaterSeaConsumption',
  'reuseDegree',
  'waterExchangeTime',
  'waterExchangeTimeMakeupwater'
]

export const tankWaterKeys: TankParameterKeys[] = [
  'totalWaterFlow',
  'makeUpWaterFlow',
  'recirculationFlow',
  'makeUpWaterFreshConsumption',
  'makeUpWaterSeaConsumption',
  'reuseDegree',
  'waterExchangeTime',
  'waterExchangeTimeMakeupwater'
]

export const deptConsumablesKeys: DepartmentParameterKeys[] = [
  'feedConsumption',
  'o2Consumption',
  'co2ConcentrationPeak',
  'tanConcentrationPeak',
  'nitrateConcentrationPeak',
  'suspendedSolidsConcentrationPeak'
]

export const tankConsumablesKeys: TankParameterKeys[] = [
  'feedConsumption',
  'o2Consumption',
  'co2Concentration',
  'tanConcentration',
  'nitrateConcentration',
  'suspendedSolidsConcentration',
  'oxygenPerFeed'
]

export const deptWasteKeys: DepartmentParameterKeys[] = ['co2Production', 'tanProduction', 'nitrateProduction', 'suspendedSolidsProduction']

export const tankWasteKeys: TankParameterKeys[] = ['co2Production', 'tanProduction', 'nitrateProduction', 'suspendedSolidsProduction']

export type FacilityParameterGroup = { label: string; keys: string[] }

// The exact grouping AxiosDashboard.vue shows when "Facility" is selected in the
// Design Parameters tree - the full parameter pool offered by the compare page.
export const facilityParameterGroups: FacilityParameterGroup[] = [
  { label: 'Fish', keys: [...periodFishKeys, ...annualFishKeys, ...deptFishKeys, ...extraFishKeys] },
  { label: 'Water Flow', keys: [...deptWaterKeys, ...minExchangeTimeKeys] },
  { label: 'Consumables and water parameters', keys: [...deptConsumablesKeys] },
  { label: 'Waste Production', keys: [...deptWasteKeys] }
]
